import React from 'react';
import logo from '../assets/AUSM-LOGO.png';
import FinancialPolicyPages from './FinancialPolicyPages';

const ApplicationPDFContent = ({ formData, studentId, applicationId }) => {
    const today = new Date().toLocaleDateString('en-GB');
    
    // Generate the exact Application ID matching the database sequential ID
    const getAppId = () => {
        if (applicationId) {
            return applicationId;
        }
        if (studentId && !studentId.includes("-")) {
            return `AUMS2601${String(studentId).padStart(3, '0')}`;
        }
        // Fallback to Civil ID suffix if pre-registration hasn't run yet
        const civilIdStr = String(formData?.civilId?.value || "");
        if (civilIdStr && civilIdStr.length >= 5) {
            return `AUMS26${civilIdStr.slice(-5)}`;
        }
        return "AUMS2601015";
    };
    const appId = getAppId();

    const SigField = ({ label, value, fullWidth = false, isWideLabel = false, lineColor = "border-black", variant = "box", gap = "gap-2" }) => (
        <div className={`flex items-baseline w-full mb-1 ${gap}`} dir="rtl">
            <span className="font-bold text-[11px] text-gray-800 whitespace-nowrap flex-shrink-0 text-right">
                {label}
            </span>
            <div className={`flex-1 border-black ${variant === 'box' ? 'border-2' : 'border-b-2'} ${lineColor} min-h-[32px] flex items-center px-2 bg-white`}>
                <span className="text-blue-700 font-bold text-[11px] break-words w-full text-right pb-1">
                    {value || ""}
                </span>
            </div>
        </div>
    );
    const RedHeader = ({ title, arabicTitle }) => (
        <div className="bg-[#c1004f] text-white px-8 py-3 font-bold flex justify-between items-center mb-4 rounded-none shadow-md border-b-2 border-[#a00042]" style={{ direction: 'ltr' }}>
            <div className="text-[14px] tracking-wider text-left leading-tight font-extrabold">{title}</div>
            {arabicTitle && (
                <div
                    className="text-[18px] font-extrabold text-right leading-tight"
                    dir="rtl"
                    style={{ fontFamily: "'Noto Sans Arabic', 'Cairo', sans-serif" }}
                >
                    {arabicTitle}
                </div>
            )}
        </div>
    );


    const Field = ({ label, value, arabicLabel, colSpan = "col-span-1", textColor = "text-blue-700" }) => {
        const isValueArabic = value && /[\u0600-\u06FF]/.test(value);

        return (
            <div className={`${colSpan} flex flex-col h-full mb-5 group`}>
                {/* Normalized Label Wrapper with extra breathing room */}
                <div className="flex justify-between items-end w-full min-h-[30px] mb-3 px-1">
                    <div
                        className="text-[10px] font-bold text-gray-800 text-left w-[48%]"
                        style={{ lineHeight: '1.2' }}
                    >
                        {label}
                    </div>
                    {arabicLabel && (
                        <div
                            dir="rtl"
                            className="text-[11px] font-bold text-gray-800 text-right w-[48%]"
                            style={{
                                fontFamily: "'Noto Sans Arabic', 'Cairo', sans-serif",
                                lineHeight: '1.2'
                            }}
                        >
                            {arabicLabel}
                        </div>
                    )}
                </div>
                {/* Classic Box model */}
                <div
                    className={`border-2 border-black px-3 py-2 text-[11px] font-bold ${textColor} min-h-[38px] flex items-center bg-white ${isValueArabic ? 'text-right justify-end' : 'text-left'}`}
                    dir={isValueArabic ? 'rtl' : 'ltr'}
                >
                    {value || ''}
                </div>
            </div>
        );
    };

    const Checkbox = ({ label, checked, arabicLabel }) => {
        // Handle both boolean and string 'true'/'false'
        const isChecked = checked === true || checked === 'true' || checked === 'Yes';

        return (
            <div className="flex flex-col gap-2 py-3 px-4 mb-2 border border-black rounded-none bg-white shadow-sm" style={{ direction: 'ltr' }}>
                <div className="flex justify-between items-center w-full gap-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 border-2 border-black flex items-center justify-center rounded-none flex-shrink-0 ${isChecked ? 'bg-[#c1004f] border-[#c1004f]' : 'bg-white'}`}>
                            {isChecked && (
                                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </div>
                        <span className="text-[11px] font-bold text-black text-left leading-tight">{label}</span>
                    </div>
                    {arabicLabel && (
                        <div
                            className="text-[11px] font-bold text-black text-right leading-tight"
                            dir="rtl"
                            style={{ fontFamily: "'Noto Sans Arabic', 'Cairo', sans-serif" }}
                        >
                            {arabicLabel}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const InfoBox = ({ children, title }) => (
        <div className="border border-[#e9ecef] rounded-lg p-3 mb-3 bg-[#fcfcfc]">
            {title && <h3 className="text-[11px] font-extrabold text-gray-800 mb-2 border-b border-gray-100 pb-1">{title}</h3>}
            {children}
        </div>
    );

    const PageHeader = () => (
        <div className="flex justify-between items-center mb-10 border-b-2 border-gray-100 pb-8 gap-4">
            <div className="flex items-center">
                <img src={logo} alt="AUMS LOGO" className="h-16 w-auto object-contain" />
            </div>
            <div className="text-right">
                <div className="bg-[#1e3a8a]/5 px-5 py-3 rounded-[12px] border border-[#1e3a8a]/10 shadow-sm">
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Academic Year: 2026/2027</p>
                </div>
            </div>
        </div>
    );

    const PageContainer = ({ pageNum, children, customHeader = null }) => (
        <div className="page p-[15mm] w-[210mm] min-h-[297mm] bg-white relative flex flex-col text-black box-border" style={{ pageBreakAfter: 'always' }}>
            {customHeader ? customHeader : <PageHeader />}
            <div className="flex-grow">
                {children}
            </div>
            <div className="flex justify-between items-center text-[9px] text-gray-400 mt-4 pt-2 border-t border-gray-100 font-semibold italic">
                <span>© {new Date().getFullYear()} American University for Medical Sciences - Kuwait</span>
                <span>Page {pageNum}</span>
            </div>
        </div>
    );

    return (
        <div className="application-pdf-container bg-white" dir="ltr" style={{ width: '210mm' }}>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&family=Noto+Sans+Arabic:wght@400;600;700;800&display=swap');
                    
                    .page {
                        font-family: 'Inter', 'Noto Sans Arabic', 'Segoe UI', Tahoma, Arial, sans-serif !important;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        text-rendering: optimizeLegibility;
                        -webkit-font-smoothing: antialiased;
                        font-variant-numeric: tabular-nums;
                    }
                    
                    .page * {
                        visibility: visible !important;
                        opacity: 1 !important;
                    }
                    
                    [dir="rtl"], .rtl, [dir="rtl"] *, .rtl * {
                        font-family: 'Noto Sans Arabic', 'Geeza Pro', 'Segoe UI', Tahoma, Arial, sans-serif !important;
                        text-align: right;
                        direction: rtl;
                        unicode-bidi: embed;
                        text-rendering: optimizeLegibility;
                        -webkit-font-smoothing: antialiased;
                        letter-spacing: normal !important;
                        word-spacing: normal !important;
                        line-height: 1.4;
                        font-weight: normal !important;
                        font-style: normal !important;
                    }
                    
                    span[dir="rtl"] {
                        font-family: 'Noto Sans Arabic', 'Geeza Pro', 'Segoe UI', Tahoma, Arial, sans-serif !important;
                        unicode-bidi: embed;
                        direction: rtl;
                        letter-spacing: normal !important;
                        word-spacing: normal !important;
                        font-weight: normal !important;
                        font-style: normal !important;
                    }
                `}
            </style>

            {/* PAGE 1: ADMISSION REQUIREMENTS */}
            <PageContainer pageNum={1}>
                <div className="text-center mb-10 space-y-1 pb-2">
                    <h1 className="text-[18px] font-extrabold text-[#b60041] tracking-tight">Application for Bachelor of Dental Medicine Program - BDM</h1>
                <div className="text-[18px] font-bold text-[#b60041] tracking-wide mt-2 text-center">
                    For Self Funded Students Only
                </div>
                </div>

                <div className="py-4 mb-4 border-b border-gray-100">
                    <div className="text-[12px] leading-relaxed text-gray-800 text-justify">
                        Admission to the Bachelor of Dental Medicine program for the academic year 2026/2027 is
                        competitive and according to the college capacity and the availability of seats. Meeting the minimum
                        requirements or submitting the required documents does not mean acceptance or admission to the
                        university.
                    </div>
                </div>

                <InfoBox title="Minimum Admission Requirements">
                    <div className="text-[11px] leading-relaxed space-y-2 text-gray-800">
                        <p><strong>1. Academic Qualifications:</strong></p>
                        <p>• A minimum 90% general secondary school average (scientific stream) or its equivalent in Kuwait, as recognized by the American University of Medical Sciences.</p>
                        <p><strong>2. English Language Proficiency:</strong></p>
                        <p>• Minimum IELTS 5.5 score or its equivalent (dated within the last two years).</p>
                        <p><strong>3. Personal Interview:</strong></p>
                        <p>• Applicants must pass the university's personal interview.</p>
                        <p><strong>4. Submission of Required Documents:</strong></p>
                        <p>• All required documents must be submitted to the university administration by the specified application period deadline.</p>
                        <p><strong>5. Fees Payment:</strong></p>
                        <p>• Applicants must pay the non-refundable application fees to be considered a valid application.</p>
                    </div>
                </InfoBox>

                <InfoBox title="Required Documents for Admission (Upload) All in PDF Format:">
                    <div className="text-[11px] leading-relaxed space-y-1 text-gray-800">
                        <p>1. Official grade transcript or certified copy.</p>
                        <p>2. Equivalency of high school degree issued by the Ministry of Education in Kuwait (for private school graduates).</p>
                        <p>3. Valid IELTS/TOEFL score report (no older than two years).</p>
                        <p>4. Copy of birth certificate.</p>
                        <p>5. Valid National ID and valid passport photocopies.</p>
                        <p>6. passport-sized photographs.</p>
                        <p>7. Certificate of good conduct (from school).</p>
                        <p>8. Valid National ID and valid passport photocopies of parents / or legal guardian.</p>
                        <p>9. Applicants aged 21 and above: provide criminal record certificate.</p>
                        <p>10. Kuwaiti Applicants: Certificate proof of citizenship of father- from Sahal App.</p>
                        <p className="text-[9px] mt-2 italic">Additional documents may be required from admission office in the College of Dental Medicine.</p>
                    </div>
                </InfoBox>
            </PageContainer>

            {/* PAGE 2: PERSONAL + NATIONALITY + ADDRESS + CONTACT */}
            <PageContainer pageNum={2}>
                <RedHeader title="Personal Details of Applicant" arabicTitle="المعلومات الشخصية للطالب المتقدم" />
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="First Name" value={formData.firstName?.value} arabicLabel="الاسم الأول" textColor="text-black" />
                    <Field label="Father Name" value={formData.middleName?.value} arabicLabel="اسم الأب" textColor="text-black" />
                    <Field label="Family Name" value={formData.lastName?.value} arabicLabel="اسم العائلة" textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="First Name (Arabic)" value={formData.arabicFirstName?.value} arabicLabel="الاسم الأول بالعربي" textColor="text-black" />
                    <Field label="Father Name (Arabic)" value={formData.arabicMiddleName?.value} arabicLabel="اسم الأب بالعربي" textColor="text-black" />
                    <Field label="Family Name (Arabic)" value={formData.arabicLastName?.value} arabicLabel="اسم العائلة بالعربي" textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Date of Birth" value={formData.dob?.value} textColor="text-black" />
                    <Field label="Gender" value={formData.gender?.value} textColor="text-black" />
                    <Field label="Marital Status" value={formData.maritalStatus?.value} textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Civil ID" value={formData.civilId?.value} textColor="text-black" />
                    <div className="col-span-2"></div>
                </div>
                <RedHeader title="Nationality and Residency" arabicTitle="الجنسية والإقامة" />
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Country of Nationality" value={formData.nationality?.value} arabicLabel="الجنسية" textColor="text-black" />
                    <Field label="Country of Birth" value={formData.birthCountry?.value} arabicLabel="بلد الميلاد" textColor="text-black" />
                    <Field label="Passport Number" value={formData.passport?.value} arabicLabel="رقم جواز السفر" textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Validity of Residence" value={formData.validityOfRes?.value} arabicLabel="صلاحية الإقامة" textColor="text-black" />
                    {formData.nationality?.value !== "Kuwait" && (
                        <Field label="Security Card" value={formData.securityCard?.value} arabicLabel="رقم البطاقة الأمنية للمقيمين بصورة غير قانونية" textColor="text-black" />
                    )}
                </div>
            </PageContainer>

            {/* PAGE 3: ADDRESS + CONTACT */}
            <PageContainer pageNum={3}>
                <RedHeader title="Address Information" arabicTitle="معلومات العنوان" />
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Block" value={formData.address?.block?.value} arabicLabel="القطعة" textColor="text-black" />
                    <Field label="Street" value={formData.address?.street?.value} arabicLabel="الشارع" textColor="text-black" />
                    <Field label="House / Apartment" value={formData.address?.appartment?.value} arabicLabel="رقم المنزل/ الشقة" textColor="text-black" />
                    <Field label="Area" value={formData.address?.area?.value} arabicLabel="المنطقة" textColor="text-black" />
                    <Field label="Jaddah" value={formData.address?.jaddah?.value} arabicLabel="جادة" textColor="text-black" />
                    <Field label="Country" value={formData.address?.country?.value} arabicLabel="البلد" textColor="text-black" />
                    <Field label="Po. Box" value={formData.address?.po?.value} arabicLabel="صندوق البريد" textColor="text-black" />
                    <Field label="Zip Code" value={formData.address?.zipCode?.value} arabicLabel="الرمز البريدي" textColor="text-black" />
                    <Field label="Governorate" value={formData.address?.governorate?.value} arabicLabel="المحافظة" textColor="text-black" />
                </div>
                <RedHeader title="Contact Information" arabicTitle="معلومات الاتصال" />
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Mobile Number (Student)" value={formData.mobileOne?.value} arabicLabel="رقم الجوال (طالب)" textColor="text-black" />
                    <Field label="Mobile Number (Parent)" value={formData.mobileTwo?.value} arabicLabel="رقم الجوال (ولي الأمر)" textColor="text-black" />
                    <Field label="Home Telephone 1" value={formData.homeTelOne?.value} arabicLabel="هاتف المنزل (1)" textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Home Telephone 2" value={formData.homeTelTwo?.value} arabicLabel="هاتف المنزل (2)" textColor="text-black" />
                    <Field label="Work Telephone" value={formData.workTel?.value} arabicLabel="رقم هاتف العمل" textColor="text-black" />
                    <Field label="Fax Telephone" value={formData.faxTel?.value} arabicLabel="رقم الفاكس" textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Student Email" value={formData.emailOne?.value} arabicLabel="البريد الإلكتروني ( طالب )" colSpan="col-span-3" textColor="text-black" />
                </div>
            </PageContainer>

            {/* PAGE 4: FATHER + MOTHER */}
            <PageContainer pageNum={4}>
                <RedHeader title="Father Details" arabicTitle="معلومات الأب" />
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Full Name" value={formData.family?.fatherName?.value} arabicLabel="الاسم بالكامل" textColor="text-black" />
                    <Field label="Full Name (Arabic)" value={formData.family?.arabicFatherName?.value} arabicLabel="الاسم بالكامل بالعربي" textColor="text-black" />
                    <Field label="Date of Birth" value={formData.family?.fatherDob?.value} arabicLabel="تاريخ الميلاد" textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Nationality" value={formData.family?.fatherNationality?.value} arabicLabel="الجنسية" textColor="text-black" />
                    <Field label="Civil ID" value={formData.family?.fatherCivilId?.value} arabicLabel="الرقم المدني" textColor="text-black" />
                    <Field label="Mobile No" value={formData.family?.fatherMobile?.value} arabicLabel="رقم الهاتف الجوال" textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Work Telephone" value={formData.family?.fatherWorkTel?.value} arabicLabel="رقم هاتف العمل" textColor="text-black" />
                    <Field label="Email Address" value={formData.family?.fatherEmail?.value} arabicLabel="البريد الالكتروني" textColor="text-black" />
                    <Field label="Employer" value={formData.family?.fatherEmployer?.value} arabicLabel="جهة العمل" textColor="text-black" />
                </div>
                <div className="grid grid-cols-3 gap-x-4 mb-2">
                    <Field label="Occupation" value={formData.family?.fatherOccup?.value} arabicLabel="المسمى الوظيفي" textColor="text-black" />
                    <Field label="Department" value={formData.family?.fatherDepartment?.value} arabicLabel="القسم" textColor="text-black" />
                    <Field label="Father Exact Job" value={formData.family?.fatherExactJob?.value} arabicLabel="حدد نوع الوظيفة" textColor="text-black" />
                </div>

                <div className="mt-8">
                    <RedHeader title="Mother Details" arabicTitle="معلومات الأم" />
                    <div className="grid grid-cols-3 gap-x-4 mb-2">
                        <Field label="Full Name" value={formData.family?.motherName?.value} arabicLabel="الاسم بالكامل" textColor="text-black" />
                        <Field label="Full Name (Arabic)" value={formData.family?.arabicMotherName?.value} arabicLabel="الاسم بالكامل بالعربي" textColor="text-black" />
                        <Field label="Date of Birth" value={formData.family?.motherDob?.value} arabicLabel="تاريخ الميلاد" textColor="text-black" />
                    </div>
                    <div className="grid grid-cols-3 gap-x-4 mb-2">
                        <Field label="Nationality" value={formData.family?.motherNationality?.value} arabicLabel="الجنسية" textColor="text-black" />
                        <Field label="Civil ID" value={formData.family?.motherCivilId?.value} arabicLabel="الرقم المدني" textColor="text-black" />
                        <Field label="Mobile No" value={formData.family?.motherMobile?.value} arabicLabel="رقم الهاتف الجوال" textColor="text-black" />
                    </div>
                    <div className="grid grid-cols-3 gap-x-4 mb-2">
                        <Field label="Work Telephone" value={formData.family?.motherWorkTel?.value} arabicLabel="رقم هاتف العمل" textColor="text-black" />
                        <Field label="Email Address" value={formData.family?.motherEmail?.value} arabicLabel="البريد الالكتروني" textColor="text-black" />
                        <Field label="Employer" value={formData.family?.motherEmployer?.value} arabicLabel="جهة العمل" textColor="text-black" />
                    </div>
                    <div className="grid grid-cols-3 gap-x-4 mb-2">
                        <Field label="Occupation" value={formData.family?.motherOccup?.value} arabicLabel="المسمى الوظيفي" textColor="text-black" />
                        <Field label="Department" value={formData.family?.motherDepartment?.value} arabicLabel="القسم" textColor="text-black" />
                        <Field label="Mother Exact Job" value={formData.family?.motherExactJob?.value} arabicLabel="حدد نوع الوظيفة" textColor="text-black" />
                    </div>
                </div>
            </PageContainer>

            {/* PAGE 5: SPONSORSHIP + HEALTH */}
            <PageContainer pageNum={5}>
                <RedHeader title="Sponsorship" arabicTitle="المصاريف الدراسية" />
                <div className="mb-3">
                    <p className="text-[11px] font-bold text-gray-800 mb-3">How do you expect to meet the cost of tuition and other expenses?</p>
                    <div className="space-y-2">
                        <Checkbox label="Parents" arabicLabel="الوالدين" checked={formData.sponsorship?.value?.parents} />
                        <Checkbox label="Myself" arabicLabel="الطالب نفسه" checked={formData.sponsorship?.value?.myself} />
                        <Checkbox label="Others (Specify)" arabicLabel="اخرى ( حدد الجهة )" checked={formData.sponsorship?.value?.others} />
                    </div>
                    {(formData.sponsorshipOtherNote?.value || formData.sponsorshipScholarshipNote?.value) && (
                        <div className="mt-4 text-xs">
                            <span className="font-bold text-gray-800">Notes : </span>
                            <span className="text-gray-700 font-bold">{formData.sponsorshipOtherNote?.value || formData.sponsorshipScholarshipNote?.value}</span>
                        </div>
                    )}
                </div>

                <RedHeader title="Health Information" arabicTitle="معلومات عن الحالة الصحية للطالب" />
                <div className="mb-3 text-xs">
                    <p className="font-bold mb-1 text-gray-800">Do you suffer from any physical, learning, mental, and disabilities that may impair your performance at the University?</p>
                    <p className="font-bold mb-2 rtl text-right text-gray-800" dir="rtl">هل تعاني من أي حالة مرضية جسدية أو نفسية أو إعاقة ؟</p>
                    <div className="flex justify-end gap-12 mb-4 bg-[#fcfcfc] p-3 border border-gray-200">
                        <div className="flex items-center gap-3">
                            <span className="font-bold text-[11px] text-gray-800">Yes / <span dir="rtl">نعم</span></span>
                            <div className={`w-5 h-5 border-2 border-[#c1004f] flex items-center justify-center rounded-sm ${formData.isHealthRecord?.value === true ? 'bg-[#c1004f]' : 'bg-white'}`}>
                                {formData.isHealthRecord?.value === true && (
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="font-bold text-[11px] text-gray-800">No / <span dir="rtl">لا</span></span>
                            <div className={`w-5 h-5 border-2 border-[#c1004f] flex items-center justify-center rounded-sm ${formData.isHealthRecord?.value === false ? 'bg-[#c1004f]' : 'bg-white'}`}>
                                {formData.isHealthRecord?.value === false && (
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    </div>

                    {formData.isHealthRecord?.value === true && (
                        <div className="mt-2">
                            <Field label="If Yes Please Indicate Details" value={formData.healthNote?.value} />
                        </div>
                    )}

                    <p className="text-[9px] font-bold text-red-700 mt-4 italic">
                        * The university does not bear any legal responsibility from concealing the student's health condition.
                    </p>
                </div>
            </PageContainer>

            {/* PAGE 6: ACADEMIC - HIGH SCHOOL & COLLEGE */}
            <PageContainer pageNum={6}>
                <RedHeader title="Educational Information" arabicTitle="المعلومات العلمية" />

                <h3 className="text-[12px] font-bold text-gray-800 mb-2 flex justify-between">
                    <span>Secondary School Education Details</span>
                    <span dir="rtl">معلومات عن التعليم الثانوي المقررات</span>
                </h3>

                <table className="w-full border-collapse border border-gray-400 text-[10px] mb-6">
                    <thead>
                        <tr className="bg-white">
                            <th className="border border-gray-400 p-2 w-[25%] text-gray-800">School Name / <span dir="rtl">اسم المدرسة</span></th>
                            <th className="border border-gray-400 p-2 w-[15%] text-gray-800">GPA / <span dir="rtl">المعدل العام</span></th>
                            <th className="border border-gray-400 p-2 w-[25%] text-gray-800">Graduation Date / <span dir="rtl">تاريخ التخرج</span></th>
                            <th className="border border-gray-400 p-2 w-[35%] text-gray-800">Degree Awarded / <span dir="rtl">الدرجة الممنوحة</span></th>
                        </tr>
                    </thead>
                    <tbody className="text-center font-bold text-gray-800">
                        {formData.school?.value?.map((school, index) => (
                            <tr key={index}>
                                <td className="border border-gray-400 p-2">{school.name?.value || ''}</td>
                                <td className="border border-gray-400 p-2">{school.gpa?.value || ''}</td>
                                <td className="border border-gray-400 p-2">{school.date?.value || ''}</td>
                                <td className="border border-gray-400 p-2">{school.degree?.value || ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <table className="w-full border-collapse border border-gray-400 text-[10px] mb-8">
                    <thead>
                        <tr>
                            <th className="border border-gray-400 p-2 bg-white text-gray-800">Physics</th>
                            <th className="border border-gray-400 p-2 bg-white text-gray-800">Chemistry</th>
                            <th className="border border-gray-400 p-2 bg-white text-gray-800">Biology</th>
                            <th className="border border-gray-400 p-2 bg-white text-gray-800">Average of scientific subjects</th>
                        </tr>
                    </thead>
                    <tbody className="text-center font-bold text-gray-800">
                        <tr>
                            <td className="border border-gray-400 p-2">{formData.scientificMarks?.value?.[0]?.physics?.value || ''}</td>
                            <td className="border border-gray-400 p-2">{formData.scientificMarks?.value?.[0]?.chemistry?.value || ''}</td>
                            <td className="border border-gray-400 p-2">{formData.scientificMarks?.value?.[0]?.biology?.value || ''}</td>
                            <td className="border border-gray-400 p-2 bg-gray-50">{formData.scientificMarks?.value?.[0]?.average?.value || ''}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="mb-6">
                    <p className="text-[11px] font-bold text-gray-800 mb-3 text-center">Have you previously attended any colleges or universities? If yes please fill out the details below</p>
                    <div className="flex justify-center gap-12 mb-4">
                        <div className="flex items-center gap-3">
                            <span className="font-bold text-[11px] text-gray-800">Yes / <span dir="rtl">نعم</span></span>
                            <div className={`w-6 h-6 border-2 border-[#c1004f] flex items-center justify-center rounded-md ${formData.isCollege?.value === true ? 'bg-[#c1004f]' : 'bg-white'}`}>
                                {formData.isCollege?.value === true && (
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="font-bold text-[11px] text-gray-800">No / <span dir="rtl">لا</span></span>
                            <div className={`w-6 h-6 border-2 border-[#c1004f] flex items-center justify-center rounded-md ${formData.isCollege?.value === false ? 'bg-[#c1004f]' : 'bg-white'}`}>
                                {formData.isCollege?.value === false && (
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <p className="text-[10px] font-bold text-gray-800 mb-2 leading-relaxed">
                    List all Colleges and Universities you attended starting with the most recent (if not applicable, skip this section)
                    <br />
                    <span dir="rtl" className="block mt-1">قائمة بأسماء الجامعات والكليات المسجل بها سابقاً ( ابتداءا من الأحدث)</span>
                </p>
                <h4 className="text-[11px] font-bold text-gray-800 mb-2">College/University Details</h4>

                <table className="w-full border-collapse border border-gray-400 text-[9px] mb-8">
                    <thead>
                        <tr className="bg-white">
                            <th className="border border-gray-400 p-2 w-[25%] text-gray-800">College Name / <span dir="rtl">اسم الجامعة/الكلية</span></th>
                            <th className="border border-gray-400 p-2 w-[15%] text-gray-800">Country / <span dir="rtl">البلد</span></th>
                            <th className="border border-gray-400 p-2 w-[15%] text-gray-800">GPA / <span dir="rtl">المعدل العام</span></th>
                            <th className="border border-gray-400 p-2 w-[15%] text-gray-800">Major / <span dir="rtl">التخصص</span></th>
                            <th className="border border-gray-400 p-2 w-[30%] text-gray-800">Certificates / <span dir="rtl">الشهادة الممنوحة</span></th>
                        </tr>
                    </thead>
                    <tbody className="text-center font-bold text-gray-800">
                        {formData.college?.value?.map((college, index) => (
                            <tr key={index}>
                                <td className="border border-gray-400 p-2">{college.name?.value || ''}</td>
                                <td className="border border-gray-400 p-2">{college.country?.value || ''}</td>
                                <td className="border border-gray-400 p-2">{college.gpa?.value || ''}</td>
                                <td className="border border-gray-400 p-2">{college.major?.value || ''}</td>
                                <td className="border border-gray-400 p-2">{college.degree?.value || ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="space-y-6">
                    <div>
                        <h4 className="text-[12px] font-bold text-gray-800">Academic Awards</h4>
                        <p className="text-[10px] font-bold text-gray-800 italic mb-3 pb-0.5" dir="rtl">اذكر، أن وجد، الأعمال الأكاديمية المتميزة التي قمت بها أو الجوائز العلمية الحاصل عليها. مع ذكر السنة</p>
                        <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 min-h-[40px] text-[11px] font-bold text-gray-800">
                            {formData.archivements?.value?.map(a => a.value).filter(Boolean).join(", ") || 'No'}
                        </div>
                    </div>

                    <div>
                        <h4 className="text-[11px] font-bold text-gray-800 leading-tight">Have you ever been subject to any academic or disciplinary action (i.e. probation, suspension, dismissal etc..) From any institution attended?</h4>
                        <p className="text-[10px] font-bold text-gray-800 italic mb-3 pb-0.5" dir="rtl">هل سبق لك خضعت لأية إجراءات تأديبية أو أكاديمية أو وضعت تحت المراقبة، او تعليق القيد، او الطرد من أي مؤسسة تعليمية من قبل؟</p>
                        <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 min-h-[40px] text-[11px] font-bold text-gray-800">
                            {(formData.isSuspended?.value === true || formData.isSuspended?.value === 1 || String(formData.isSuspended?.value).toLowerCase() === 'true' || String(formData.isSuspended?.value).toLowerCase() === 'yes' || String(formData.isSuspended?.value) === '1' || (formData.suspensionNote?.value && formData.suspensionNote?.value.trim() !== '')) ? (
                                <>Yes / <span dir="rtl">نعم</span></>
                            ) : (
                                <>No / <span dir="rtl">لا</span></>
                            )}
                        </div>
                    </div>

                    {(formData.isSuspended?.value === true || formData.isSuspended?.value === 1 || String(formData.isSuspended?.value).toLowerCase() === 'true' || String(formData.isSuspended?.value).toLowerCase() === 'yes' || String(formData.isSuspended?.value) === '1' || (formData.suspensionNote?.value && formData.suspensionNote?.value.trim() !== '')) && (
                        <div>
                            <h4 className="text-[11px] font-bold text-gray-800 leading-tight">If Yes Please Indicate Details / <span dir="rtl">اذكر التفاصيل اذا كانت الإجابة بنعم</span></h4>
                            <p className="text-[10px] font-bold text-gray-800 italic mb-3 pb-0.5" dir="rtl">اذكر التفاصيل اذا كانت الإجابة بنعم</p>
                            <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 min-h-[40px] text-[11px] font-bold text-gray-800">
                                {formData.suspensionNote?.value || ''}
                            </div>
                        </div>
                    )}
                </div>
            </PageContainer>

            {/* PAGE 7: ENGLISH TEST + EMERGENCY */}
            <PageContainer pageNum={7}>
                <RedHeader title="English Proficiency Test" arabicTitle="اختبار كفاءة اللغة الإنجليزية" />
                <table className="w-full border-collapse border border-gray-800 text-[12px] mb-8">
                    <thead>
                        <tr className="bg-white">
                            <th className="border border-gray-800 p-3 text-left w-[33%] text-gray-800">Score</th>
                            <th className="border border-gray-800 p-3 text-left w-[33%] text-gray-800">Date</th>
                            <th className="border border-gray-800 p-3 text-left w-[34%] text-gray-800">Test Type</th>
                        </tr>
                    </thead>
                    <tbody className="font-bold text-gray-800">
                        <tr>
                            <td className="border border-gray-800 p-3">{formData.test?.testScore?.value || ''}</td>
                            <td className="border border-gray-800 p-3">{formData.test?.testDate?.value || ''}</td>
                            <td className="border border-gray-800 p-3 uppercase">{formData.test?.testType?.value || ''}</td>
                        </tr>
                    </tbody>
                </table>

                <RedHeader title="Emergency Contact" arabicTitle="الاتصال في حالات الطوارئ" />
                <p className="text-[9px] font-bold text-gray-800 mb-2">Indicate below the names and Civil ID number of 2 persons to be contacted in case of emergency</p>
                {formData.emergency?.map((contact, idx) => contact.name?.value && (
                    <div key={idx} className="mb-4">
                        <div className="flex justify-between items-center mb-3 pb-2 border-b-2 border-gray-100">
                            <span className="text-[10px] font-bold text-[#c1004f] uppercase tracking-wider">CONTACT {idx + 1}</span>
                            <span className="text-[10px] font-bold text-gray-600">EMERGENCY CONTACT DETAILS</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                            <Field label="Name" value={contact.name?.value} arabicLabel="الاسم" textColor="text-black" />
                            <Field label="Civil ID" value={contact.civilId?.value} arabicLabel="الرقم المدني" textColor="text-black" />
                            <Field label="Relationship" value={contact.relationship?.value} arabicLabel="صلة القرابة" textColor="text-black" />
                            <Field label="Telephone" value={contact.contact?.value} arabicLabel="هاتف" textColor="text-black" />
                        </div>
                    </div>
                ))}

            </PageContainer>

            {/* PAGE 8: DECLARATION & PLEDGE */}
            <PageContainer pageNum={8}>
                <div className="text-center mb-6">
                    <h2 className="text-[#b60041] font-bold text-[20px]">Declaration and Pledge / <span dir="rtl">الإقرار و التعهد</span></h2>
                </div>

                <div className="space-y-4 mb-8">
                    {/* Header Row */}
                    <div className="grid grid-cols-2 gap-10 border-b-2 border-gray-200 pb-2">
                        <p className="font-bold text-[12px] text-[#b60041]">Declaration and Acknowledgment</p>
                        <p className="font-bold text-[14px] text-[#b60041] text-right" dir="rtl">إقرار بالعلم و التعهد لتقديم طلب الالتحاق</p>
                    </div>

                    {/* Point 1 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800">
                        <p>• I, the undersigned, certify that the information provided in this application is correct and complete. I grant the American University for Medical Sciences or any relevant official body the full right to verify it or use the attached documents, as they deem appropriate.</p>
                        <p className="text-right font-normal" dir="rtl">• أنا الموقع أدناه، أقر بصحة واكتمال المعلومات الواردة في هذا الطلب. وأمنح الجامعة الأمريكية للعلوم الطبية أو أي جهة رسمية مختصة الحق الكامل في التحقق منها أو استخدام المستندات المرفقة بما تراه مناسبًا.</p>
                    </div>

                    {/* Point 2 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800">
                        <p>• I authorize the American University for Medical Sciences to use the information provided by me for its educational purposes.</p>
                        <p className="text-right font-normal" dir="rtl">• أصرح للجامعة الأمريكية للعلوم الطبية باستخدام المعلومات التي قدمتها لأغراضها التعليمية.</p>
                    </div>

                    {/* Point 3 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800">
                        <p>• I also grant the American University for Medical Sciences the right to reject my application, cancel my enrollment, or expel me from the university if they discover forgery or inaccuracy in any of the information provided or any of the attached documents.</p>
                        <p className="text-right font-normal" dir="rtl">• كما أمنح الجامعة الأمريكية للعلوم الطبية الحق في رفض طلبي، أو إلغاء تسجيلي، أو فصلي من الجامعة إذا اكتشفت تزويرًا أو عدم دقة في أي من المعلومات المقدمة أو المستندات المرفقة.</p>
                    </div>

                    {/* Point 4 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800">
                        <p>• The American University for Medical Sciences has the right to accept or reject any application/request without having to justify any reason, and the university also has the right to retain all fees paid in the event of this occurrence.</p>
                        <p className="text-right font-normal" dir="rtl">• يحق للجامعة الأمريكية للعلوم الطبية قبول أو رفض أي طلب دون إبداء أي أسباب، كما يحق للجامعة الاحتفاظ بجميع الرسوم المدفوعة في حال حدوث ذلك.</p>
                    </div>

                    {/* Point 5 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800">
                        <p>• I also pledge, if accepted into the university, to complete and finalize all relevant procedures.</p>
                        <p className="text-right font-normal" dir="rtl">• كما أتعهد، في حال قبولي في الجامعة، بإكمال جميع الإجراءات ذات الصلة وإتمامها.</p>
                    </div>

                    {/* Point 6 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800">
                        <p>• The information provided by me is considered the property of the college, which has the right to retain all documents submitted by me.</p>
                        <p className="text-right font-normal" dir="rtl">• تعد المعلومات التي قدمتها ملكًا للكلية، ولها الحق في الاحتفاظ بجميع المستندات التي قدمتها.</p>
                    </div>

                    {/* Pledge Sub-Header */}
                    <div className="grid grid-cols-2 gap-4 pt-1">
                        <p className="font-bold text-[10px] text-gray-800">• I also agree and pledge, during my admission and study period at the university:</p>
                        <p className="font-bold text-[12px] text-right text-gray-800" dir="rtl"> أتعهد وأوافق، خلال فترة قبولي ودراستي في الجامعة، بما يلي :</p>
                    </div>

                    {/* Pledge Points */}
                    {/* Pledge Point 1 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800 border-t border-gray-50 pt-2">
                        <p>1. To adhere to and fully respect the rules and regulations of the American University for Medical Sciences and the decisions issued by the university. Any violation thereof will subject me to the penalty determined by the university.</p>
                        <p className="text-right font-normal" dir="rtl">.1 الالتزام التام بقواعد ولوائح الجامعة الأمريكية للعلوم الطبية والقرارات الصادرة عنها. وأي مخالفة لذلك ستعرضني للعقوبة التي تقررها الجامعة.</p>
                    </div>

                    {/* Pledge Point 2 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800">
                        <p>2. I also pledge to behave and conduct myself in a respectful manner, to address and treat all university staff and colleagues with respect, and to avoid anything that offends modesty or violates the traditions and customs of Kuwaiti society, whether during the admissions process or after admission.</p>
                        <p className="text-right font-normal" dir="rtl">.2 كما أتعهد بالتصرف بأدب واحترام، ومخاطبة جميع موظفي الجامعة وزملائي باحترام، وتجنب كل ما يخدش الحياء أو يخالف تقاليد وعادات المجتمع الكويتي، سواء أثناء عملية القبول أو بعده.</p>
                    </div>

                    {/* Pledge Point 3 */}
                    <div className="grid grid-cols-2 gap-4 text-[10px] leading-relaxed items-start text-gray-800">
                        <p>3. I also pledge to preserve the university's reputation and refrain from defaming it or spreading rumors about it or its staff, whether inside or outside the university campus.</p>
                        <p className="text-right font-normal" dir="rtl">.3 كما أتعهد بالحفاظ على سمعة الجامعة، والامتناع عن التشهير بها أو نشر الشائعات عنها أو عن موظفيها، سواءً داخل الحرم الجامعي أو خارجه.</p>
                    </div>
                </div>

                <div className="mb-6 px-4">
                    <div className="flex items-center gap-4 p-4 border-2 border-[#c1004f]/40 rounded-xl bg-[#c1004f]/5">
                        <div className="w-7 h-7 bg-[#c1004f] flex items-center justify-center rounded-md shadow-sm flex-shrink-0">
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div className="flex-1 font-extrabold text-[12px] text-gray-900 leading-none py-1">
                            I agreed to the terms and condition above / <span dir="rtl">أوافق على الشروط والأحكام المذكورة أعلاه</span>
                        </div>
                    </div>
                </div>

                <div className="py-2">
                    <p className="text-gray-800 text-[13px] text-right" dir="rtl">
                        أقر أنني أوافق على جميع الشروط والأحكام المذكورة أعلاه.
                    </p>

                    <div className="space-y-1 mt-6 text-right" dir="rtl">
                        <div className="flex items-baseline gap-1 w-full" dir="rtl">
                            <span className="font-bold text-[11px] text-gray-800 whitespace-nowrap" dir="rtl">أنا :</span>
                            <div className="flex-1 border-b-2 border-black min-h-[32px] flex items-center px-2 bg-white">
                                <span className="text-blue-700 font-bold text-[11px] w-full text-right pb-1">
                                    {formData.arabicFirstName?.value || ''} {formData.arabicMiddleName?.value || ''} {formData.arabicLastName?.value || ''}
                                </span>
                            </div>
                            <span className="font-bold text-[11px] text-gray-800 whitespace-nowrap px-2" dir="rtl">حامل البطاقة المدنية رقم :</span>
                            <div className="w-[180px] border-b-2 border-black min-h-[32px] flex items-center px-2 bg-white">
                                <span className="text-blue-700 font-bold text-[11px] tracking-widest w-full text-right pb-1">
                                    {formData.civilId?.value || ''}
                                </span>
                            </div>
                        </div>

                        <div className="pt-2 space-y-1">
                            <div className="grid grid-cols-2 gap-4">
                                <SigField label="توقيع الطالب:" value="" lineColor="border-[#c1004f]" variant="line" fullWidth gap="gap-1" />
                                <SigField label="التاريخ:" value="" variant="line" fullWidth gap="gap-1" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <SigField label="اسم ولي الأمر:" value={`${formData.family?.arabicFatherName?.value || ''}`} variant="line" gap="gap-1" />
                                <SigField label="الرقم المدني:" value={formData.family?.fatherCivilId?.value} variant="line" gap="gap-1" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <SigField
                                    label="العنوان:"
                                    gap="gap-1"
                                    value={[
                                        formData?.address?.area?.value ? `منطقة ${formData.address.area.value}` : "",
                                        formData?.address?.block?.value ? `قطعة ${formData.address.block.value}` : "",
                                        formData?.address?.street?.value ? `شارع ${formData.address.street.value}` : ""
                                    ].filter(Boolean).join(" - ")}
                                    variant="line"
                                    fullWidth
                                />
                                <SigField label="رقم الجوال:" value={formData.family?.fatherMobile?.value} variant="line" fullWidth />
                            </div>

                            <div className="grid grid-cols-2 gap-16">
                                <SigField label="توقيع ولي الأمر:" value="" isWideLabel lineColor="border-[#c1004f]" variant="line" fullWidth />
                                <SigField label="التاريخ:" value="" variant="line" fullWidth />
                            </div>
                        </div>
                    </div>
                </div>
            </PageContainer>

            <FinancialPolicyPages formData={formData} today={today} appId={appId} PageContainer={PageContainer} />
        </div>
    );
};

export default ApplicationPDFContent;
